import type { PaymentQrCompanyId } from './payment-qr.model';
import type { QrMerchantCandidate, QrMerchantOption } from './qr-merchant.model';

/**
 * Reglas puras del campo "Comercio" del cobro QR: elegibilidad, identificador
 * que viaja en `id_company`, etiquetas desambiguadas, orden y preselección.
 * TypeScript puro, sin Angular.
 */

/** Tope de comercios por petición; el servidor acota `limit` a [1, 200]. */
export const QR_MERCHANTS_LIMIT = 200;
/** Valor de `cobra_a` que identifica a un comercio con cuenta propia en la pasarela. */
export const QR_OWN_ACCOUNT_COBRA_A = 'cuenta propia';

/** Separador entre la etiqueta base y su discriminador (RS6). */
const LABEL_SEPARATOR = ' · ';

/**
 * ⚠️ LA regla del supuesto (requirements §3, P1): el único lugar del código que
 * decide qué identificador viaja en `id_company`.
 *
 * SUPUESTO: el back espera el identificador de BANCA, igual que links
 * (`org_company_id` = PK de la org; si no, `external_ref` = UUID del tenant).
 * NUNCA el `id` de Payments: es otro espacio de identificadores y un `5` de
 * Payments puede coincidir con la empresa `5` de banca. Si backend confirma el
 * `id` de Payments, se cambia SOLO esta función (y su prueba).
 *
 * Devuelve el valor SIN convertir (RS17): un `org_company_id` numérico sale
 * número; uno textual sale texto.
 */
export function resolveQrCompanyId(candidate: QrMerchantCandidate): PaymentQrCompanyId | null {
  const org = candidate.orgCompanyId;
  if (org !== null && String(org).trim() !== '') return org;
  const ref = candidate.externalRef.trim();
  return ref !== '' ? ref : null;
}

/**
 * Elegible (RS3) = activo (o sin dato) + cuenta propia (sin distinguir
 * mayúsculas ni espacios) + se le puede mandar un `id_company`. La condición (c)
 * se expresa con `resolveQrCompanyId` a propósito: así "elegible" y "enviable"
 * no pueden divergir. `gateway_account_id` NO es condición (design §2.2).
 */
export function isQrEligibleMerchant(candidate: QrMerchantCandidate): boolean {
  return (
    candidate.isActive !== false &&
    candidate.cobraA.trim().toLowerCase() === QR_OWN_ACCOUNT_COBRA_A &&
    resolveQrCompanyId(candidate) !== null
  );
}

/** Etiqueta base (RS5): nombre comercial → razón social → `#<id de Payments>`. */
function baseLabel(candidate: QrMerchantCandidate): string {
  return candidate.name.trim() || candidate.legalName.trim() || `#${String(candidate.paymentsId)}`;
}

function norm(value: string): string {
  return value.trim().toLocaleLowerCase('es');
}

interface Eligible {
  readonly candidate: QrMerchantCandidate;
  readonly base: string;
  readonly companyId: PaymentQrCompanyId;
}

/** Discriminadores en orden de preferencia (RS6). El último siempre resuelve: el PK es único. */
const DISCRIMINATORS: readonly ((c: QrMerchantCandidate) => string)[] = [
  (c) => c.legalName.trim(),
  (c) => c.gatewayAccountId.trim(),
  (c) => `#${String(c.paymentsId)}`,
];

/**
 * Busca el primer discriminador que, para TODOS los miembros del grupo, no esté
 * vacío, sea distinto de su base y deje las etiquetas del grupo sin repetirse.
 */
function labelsForGroup(group: readonly Eligible[]): Map<Eligible, string> {
  const labels = new Map<Eligible, string>();
  if (group.length === 1) {
    labels.set(group[0], group[0].base);
    return labels;
  }
  for (const discriminator of DISCRIMINATORS) {
    const seen = new Set<string>();
    let valid = true;
    labels.clear();
    for (const member of group) {
      const disc = discriminator(member.candidate);
      if (disc === '' || norm(disc) === norm(member.base)) {
        valid = false;
        break;
      }
      const label = `${member.base}${LABEL_SEPARATOR}${disc}`;
      const key = norm(label);
      if (seen.has(key)) {
        valid = false;
        break;
      }
      seen.add(key);
      labels.set(member, label);
    }
    if (valid) return labels;
  }
  // Inalcanzable: el discriminador por PK siempre cumple. Se deja la base por seguridad de tipos.
  labels.clear();
  for (const member of group) labels.set(member, member.base);
  return labels;
}

function compareOptions(a: QrMerchantOption, b: QrMerchantOption): number {
  const byLabel = a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
  return byLabel !== 0 ? byLabel : a.key.localeCompare(b.key, 'es', { numeric: true });
}

/**
 * Candidatos → opciones del campo (RS3–RS6): una pasada para filtrar y armar,
 * un `Map` por etiqueta normalizada para agrupar (O(n)) y un `sort` final (RS4).
 */
export function buildQrMerchantOptions(candidates: readonly QrMerchantCandidate[]): QrMerchantOption[] {
  const groups = new Map<string, Eligible[]>();
  for (const candidate of candidates) {
    if (!isQrEligibleMerchant(candidate)) continue;
    const companyId = resolveQrCompanyId(candidate);
    if (companyId === null) continue; // ya lo excluye `isQrEligibleMerchant`; estrecha el tipo
    const base = baseLabel(candidate);
    const key = norm(base);
    const group = groups.get(key);
    const eligible: Eligible = { candidate, base, companyId };
    if (group) {
      group.push(eligible);
    } else {
      groups.set(key, [eligible]);
    }
  }

  const options: QrMerchantOption[] = [];
  for (const group of groups.values()) {
    const labels = labelsForGroup(group);
    for (const member of group) {
      options.push({
        key: String(member.candidate.paymentsId),
        label: labels.get(member) ?? member.base,
        companyId: member.companyId,
        orgCompanyId: member.candidate.orgCompanyId,
        externalRef: member.candidate.externalRef,
      });
    }
  }
  return options.sort(compareOptions);
}

export interface QrMerchantPreference {
  /** Llave conservada de la emisión anterior en la misma ventana ("Generar otro", RS18). */
  readonly keepKey?: string | null;
  /** `id_company` de la entidad activa de la barra lateral (identificador de BANCA). */
  readonly activeCompanyRef?: PaymentQrCompanyId | null;
}

/** Forma comparable de un identificador; `null`/vacío no coincide con nada. */
function refOf(value: PaymentQrCompanyId | null | undefined): string {
  return value === null || value === undefined ? '' : String(value).trim().toLowerCase();
}

/**
 * Preselección (RS8): (1) la llave conservada si sigue entre las opciones;
 * (2) la primera opción cuyo `org_company_id` o `external_ref` coincida con la
 * entidad activa; (3) la primera. NUNCA compara `key` (el `id` de Payments) con
 * la entidad activa (RS9): son espacios de identificadores distintos.
 */
export function pickInitialMerchantKey(
  options: readonly QrMerchantOption[],
  prefs: QrMerchantPreference = {},
): string | null {
  const keep = prefs.keepKey ?? null;
  if (keep !== null && options.some((o) => o.key === keep)) return keep;

  const active = refOf(prefs.activeCompanyRef);
  if (active !== '') {
    const match = options.find((o) => refOf(o.orgCompanyId) === active || refOf(o.externalRef) === active);
    if (match) return match.key;
  }

  return options[0]?.key ?? null;
}
